import { map } from 'lodash'
import { useCallback, useMemo } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import {
  type useEducationLevelCriteria_query$data,
  type useEducationLevelCriteria_query$key
} from '@/__generated__/useEducationLevelCriteria_query.graphql'
import { type useEducationLevelCriteriaMutation } from '@/__generated__/useEducationLevelCriteriaMutation.graphql'

type CommitAsyncFn = (educationLevelIds: string[]) => Promise<useEducationLevelCriteria_query$data>

export const useEducationLevelCriteria = (
  queryRef?: useEducationLevelCriteria_query$key | null | undefined
): [string[], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useEducationLevelCriteria_query on Query {
        currentUser {
          educationLevelCriteria(orderBy: [EDUCATION_LEVEL_BY_EDUCATION_LEVEL_ID__SEQUENCE_ASC]) {
            nodes {
              educationLevelId
            }
          }
        }
      }
    `,
    queryRef
  )
  const data = useMemo(
    () => map(queryData?.currentUser?.educationLevelCriteria.nodes, 'educationLevelId'),
    [queryData?.currentUser?.educationLevelCriteria.nodes]
  )
  const [commit, isPending] = useMutation<useEducationLevelCriteriaMutation>(graphql`
    mutation useEducationLevelCriteriaMutation($input: UpdateEducationLevelCriteriaInput!) {
      updateEducationLevelCriteria(input: $input) {
        query {
          ...useEducationLevelCriteria_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (educationLevelIds: string[]) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              // @ts-ignore false negative
              resolve(response)
            }
          },
          variables: {
            input: {
              educationLevelIds
            }
          }
        })
      ),
    [commit]
  )

  return [data, commitAsync, isPending]
}
